// The alert cell — a project's open attention flags, at a glance.
//
// This is what survived of the retired Customer Support attention queue. That
// page's whole job was "which conversations need a human right now"; folding it
// into the Explorer would have lost that unless the signal came along, so it is
// a column on every project table instead of a screen of its own.
//
// Both modes carry it (`attention.PROJECT_FLAG_COLUMNS_SQL` feeds the full and the
// support listings alike), so an alert means the same thing here as in the Alerts
// worklist — and `attention_flags` is about the BUYER's conversation, so it is
// CS's business to see.
import { Badge } from "@recruiter/shared";

/** Reasons carry different urgency; the tone makes a long table scannable.
 *  An unknown reason falls back to neutral rather than crashing — the backend
 *  vocabulary can grow ahead of this map. Kept in step with the Alerts page. */
function reasonTone(reason: string): "danger" | "warning" | "neutral" {
  if (reason === "agent_error" || reason === "chat_misuse") return "danger";
  if (reason === "human_requested" || reason === "client_waiting" || reason === "frustration") {
    return "warning";
  }
  // `seller_intent` never reaches this cell: it is a TAG-ONLY flag reason
  // (`recruiter/repo/attention.TAG_ONLY_REASONS`), excluded from the count and
  // the reasons array this renders, and shown beside the project name as the
  // "Talent, not a client" tag instead. Mira redirects those people in the same
  // turn, so it was a row nobody could work sitting in a column that promises
  // otherwise.
  return "neutral";
}

/** The worst tone among the open reasons — what the row's single count badge
 *  should shout. Danger beats warning beats neutral. */
function worstTone(reasons: readonly string[]): "danger" | "warning" | "neutral" {
  const tones = reasons.map(reasonTone);
  if (tones.includes("danger")) return "danger";
  if (tones.includes("warning")) return "warning";
  return "neutral";
}

/** How many reason chips to show before collapsing to a count. Two fits a table
 *  cell; beyond that the count is the useful signal and the detail belongs on
 *  the project page. */
const MAX_REASON_CHIPS = 2;

export function AlertCell({ count, reasons }: { count: number; reasons: readonly string[] }) {
  if (count === 0) {
    // An explicit "clear" rather than an empty cell: blank reads as missing
    // data, and "no alerts" is a real, reassuring answer.
    return <span className="rc-cell-sub">Clear</span>;
  }

  const shown = reasons.slice(0, MAX_REASON_CHIPS);
  const hidden = reasons.length - shown.length;

  return (
    <span className="ad-alert-cell">
      <Badge tone={worstTone(reasons)} size="sm">
        {count} {count === 1 ? "alert" : "alerts"}
      </Badge>
      {shown.map((reason) => (
        <Badge key={reason} tone={reasonTone(reason)} size="sm">
          {reason.replaceAll("_", " ")}
        </Badge>
      ))}
      {hidden > 0 ? <span className="rc-cell-sub">+{hidden} more</span> : null}
    </span>
  );
}

/** Sort value for an alert column: more urgent and more numerous sorts first
 *  when descending. Encodes tone as the major key so a single `agent_error`
 *  outranks five neutral flags — the point of the column is triage order, not
 *  arithmetic. */
export function alertSortValue(count: number, reasons: readonly string[]): number {
  if (count === 0) return 0;
  const weight = { danger: 3, warning: 2, neutral: 1 }[worstTone(reasons)];
  return weight * 1000 + count;
}
